import {
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@up2stay/up2promo-ui';
import { Info } from 'lucide-react';

import { useDictionary } from '@/core/providers/dictionary-provider';

type SettingsInfoHintProps = {
  /** Field/section title; the trigger is announced as "Info: <label>". */
  label: string;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
};

/**
 * Near-element help: info icon + Popover.
 * Prefer this (or SettingsGroupFooter) over inline explainer paragraphs.
 */
export function SettingsInfoHint({
  label,
  children,
  className,
  contentClassName,
  side = 'top',
  align = 'start',
}: SettingsInfoHintProps) {
  const dict = useDictionary();
  // Distinct from the field's own label so screen readers (and getByLabel)
  // don't hit two controls with the same name.
  const triggerLabel = dict.ui.infoHint.replace('{label}', label);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors',
            'hover:bg-muted/60 hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
          aria-label={triggerLabel}
        >
          <Info className="size-3.5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className={cn(
          'max-w-[16rem] px-3 py-2.5 text-[12.5px] leading-snug text-muted-foreground',
          contentClassName,
        )}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
